import saveAs from 'file-saver';
import invariant from 'invariant';
import JSZip from 'jszip';
import { t } from '~app/core/AppBundleContext';
import * as GDrive from '~app/core/googleDrive';
import { Model } from '~app/core/model';
import readFileAsync from '~app/core/readFileAsync';
import { actions as DocumentActions } from '~app/features/document';
import { actions as CanvasActions } from '~app/features/canvas';
import Diagram from '~app/models/Diagram';
import { actions as AppActions } from '~app/features/app';
import { actions as GradingActions } from '~app/features/gradingTool';
import { actions as ModelActions } from '~app/features/landing';

import saveLoadSlice from './slice';

import Logging from '~app/core/logging';
const log = Logging.getLogger('app.features.saveLoad.index');

function doLoad(loader, fileInfo) {
  return async (dispatch, getState) => {
    dispatch(AppActions.startLoading({ message: t('loading-file') }));
    let data;
    try {
      data = await loader();
      //console.log(data);
    } catch (e) {
      log.error(e);
      dispatch(AppActions.finishLoading({ success: false, error: e.message }));
      return;
    }
    let [model, ...err] = Diagram.fromJsonString(data);
    if (!model) {
      const error = t(...err);
      log.debug(error);
      dispatch(AppActions.finishLoading({ success: false, error }));
      return;
    }

    const modelType = getState().model.type;
    // we do not want to check the model type in grading mode for now
    // will always go here first
    if (!modelType && !getState().grading?.inGradingMode) {
      dispatch(ModelActions.setModelType({ type: model.type }));
      // return if graph
      if (model.type === 'graph') return;
    }
    if (model.type !== 'graph') {
      // if not graph set the model type as is
      model = model.setModelType(model.type);
    } else if (modelType) {
      // updated model type from popup
      model = model.setModelType(modelType);
    }

    const editMode =
      model.mode === 'creation' && !getState().grading?.inGradingMode
        ? 'solution'
        : getState().canvas.defaultEditMode;
    model = model.setEditMode(editMode);
    dispatch(
      DocumentActions.loadModel({ doc: Model.serialize(model), fileInfo }),
    );
    dispatch(CanvasActions.zoomToFit());
    dispatch(AppActions.finishLoading({ success: true }));
  };
}

export function loadFromJSON({ data }) {
  return async (dispatch, getState) => {
    let [model, ...err] = Diagram.fromJsonString(data);
    const fileInfo = { source: 'url', name: 'example' };
    if (!model) {
      const error = t(...err);
      log.debug(error);
      dispatch(AppActions.finishLoading({ success: false, error }));
      return;
    }
    const modelType = getState().model.type;
    if (!modelType && !getState().grading?.inGradingMode) {
      dispatch(ModelActions.setModelType({ type: model.type }));
    }
    const editMode = getState().canvas.defaultEditMode;
    model = model.setEditMode(editMode);
    dispatch(
      DocumentActions.loadModel({ doc: Model.serialize(model), fileInfo }),
    );
    dispatch(CanvasActions.zoomToFit());
    dispatch(AppActions.finishLoading({ success: true }));
  };
}

export function loadFromLocal({ file }) {
  const loader = () => readFileAsync(file);
  return doLoad(loader, { source: 'local', name: file.name });
}

export function saveToLocal({ fileName } = {}) {
  return (dispatch, getState) => {
    log.debug('in saveToLocal');
    const doc = getState().document.present;
    if (!fileName) fileName = `${doc.name}.model`;
    const model = Model.deserialize(doc, Diagram);
    invariant(model.getIsValid()[0], 'Diagram is not valid');
    const text = JSON.stringify(model.serializeForSave(), null, 2);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    try {
      saveAs(blob, fileName);
      dispatch(
        saveLoadSlice.actions.fileSaved({ source: 'local', name: fileName }),
      );
    } catch (e) {
      log.error(e);
      dispatch(AppActions.showError({ message: e.message }));
    }
    log.debug('saveToLocal done');
  };
}

export function loadDriveApi() {
  return async dispatch => {
    try {
      await GDrive.loadApiScript(process.env.GOOGLE_SDK_URL);
      await GDrive.loadLibs();
      await GDrive.initClient();
      dispatch(saveLoadSlice.actions.driveApiLoadSuccess());
    } catch (e) {
      log.error(e.details || e.message || e.error || e);
    }
  };
}

export function signIn() {
  return async dispatch => {
    try {
      await GDrive.signIn();
      dispatch(saveLoadSlice.actions.driveApiSignedIn());
    } catch (e) {
      log.error(e.details || e.message || e.error || e);
    }
  };
}

export function listFiles() {
  return async (dispatch, getState) => {
    if (!getState().saveLoad.google.isSignedIn) {
      await dispatch(signIn());
      if (!getState().saveLoad.google.isSignedIn) {
        return;
      }
    }
    dispatch(saveLoadSlice.actions.driveFileListStarted({ first: true }));
    try {
      const response = await GDrive.listFiles();
      dispatch(
        saveLoadSlice.actions.driveFileListResults({ ...response.result }),
      );
    } catch (e) {
      log.error(e.details || e.message || e.error || e);
      // TODO: show error?
      dispatch(saveLoadSlice.actions.driveFileListResults({ files: [] }));
    }
  };
}

export function listMoreFiles() {
  return async (dispatch, getState) => {
    if (!getState().saveLoad.google.isSignedIn) {
      await dispatch(signIn());
      if (!getState().saveLoad.google.isSignedIn) {
        return;
      }
    }
    dispatch(saveLoadSlice.actions.driveFileListStarted({ first: false }));
    const { nextPageToken } = getState().saveLoad.google;
    try {
      const response = await GDrive.listFiles(nextPageToken);
      dispatch(
        saveLoadSlice.actions.driveFileListResults({ ...response.result }),
      );
    } catch (e) {
      log.error(e.details || e.message || e.error || e);
      // TODO: show error?
      dispatch(
        saveLoadSlice.actions.driveFileListResults({
          files: [],
          nextPageToken,
        }),
      );
    }
  };
}

export function loadFromDrive({ file }) {
  const loader = async () => {
    const { body } = await GDrive.load(file.id);
    return body;
  };
  return doLoad(loader, { source: 'drive', ...file });
}

export function saveToDrive({ file: oldFile, fileName, overwrite }) {
  return async (dispatch, getState) => {
    if (!getState().saveLoad.google.isSignedIn) {
      await dispatch(signIn());
      if (!getState().saveLoad.google.isSignedIn) {
        return;
      }
    }

    const doc = getState().document.present;
    if (!fileName) {
      fileName = oldFile?.name ?? `${doc.name}.model`;
    }

    const file = oldFile ?? (await GDrive.getFileByName(fileName));
    if (file && !overwrite) {
      dispatch(saveLoadSlice.actions.showFileExistsPrompt(file));
      return;
    } else if (file) {
      await GDrive.deleteFile(file.id);
    }

    const model = Model.deserialize(doc, Diagram);
    invariant(model.getIsValid()[0], 'Diagram is not valid');
    const text = JSON.stringify(model.serializeForSave(), null, 2);
    try {
      await GDrive.save(text, { fileName, description: doc.name });
      dispatch(
        saveLoadSlice.actions.fileSaved({ source: 'drive', name: fileName }),
      );
    } catch (e) {
      log.error(e);
      dispatch(AppActions.showError({ message: e.message }));
    }
  };
}

export function exportToLocal({ baseName } = {}) {
  return async (dispatch, getState) => {
    const doc = getState().document.present;
    if (!baseName) baseName = doc.name;
    baseName = baseName.replace(/ /g, '_');
    const model = Model.deserialize(doc, Diagram);
    invariant(model.getIsValid()[0], 'Diagram is not valid');

    const zip = new JSZip();
    zip.file('README.txt', t('zip-readme-content'));
    zip.file(
      `${baseName}_reference.model`,
      JSON.stringify(
        model.serializeForSave({ outputType: 'creation' }),
        null,
        2,
      ),
    );
    zip.file(
      `${baseName}_self-check.model`,
      JSON.stringify(
        model.serializeForSave({ outputType: 'reference' }),
        null,
        2,
      ),
    );
    zip.file(
      `${baseName}_assessment.model`,
      JSON.stringify(
        model.serializeForSave({ outputType: 'assessment' }),
        null,
        2,
      ),
    );
    const blob = await zip.generateAsync({ type: 'blob' });
    try {
      saveAs(blob, `${baseName}.zip`);
      dispatch(
        saveLoadSlice.actions.fileSaved({ export: true, name: baseName }),
      );
    } catch (e) {
      log.error(e);
      dispatch(AppActions.showError({ message: e.message }));
    }
  };
}

export function getTimeStamp(dateTime) {
  const pad2 = n => (n < 10 ? '0' + n : n);
  const millisecondTime = dateTime ? new Date(dateTime) : new Date();
  return (
    millisecondTime.getFullYear().toString() +
    pad2(millisecondTime.getMonth() + 1) +
    pad2(millisecondTime.getDate()) +
    pad2(millisecondTime.getHours()) +
    pad2(millisecondTime.getMinutes())
  );
}

export function exportGrades({ csv, imageBlobs, baseName, timestamp } = {}) {
  return async (dispatch, getState) => {
    const doc = getState().document.present;
    const refFileContent = getState().grading.referenceFile;
    baseName = baseName ?? doc.name;
    //Remove extra spaces and replace single space with underscore
    baseName = baseName.replace(/\s+/g, ' ').trim().replace(/ /g, '_');
    const zip = new JSZip();
    // store reference file in the results zip
    zip.file(`${baseName}_reference.model`, refFileContent);
    zip.file(`${baseName}_results_${timestamp}.csv`, csv);
    for (const image of imageBlobs) {
      const imageBlob = await fetch(image.blob).then(r => r.blob());
      zip.file(image.filename, imageBlob);
      URL.revokeObjectURL(image.blob);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    try {
      saveAs(blob, `${baseName}_results_${timestamp}.zip`);
      dispatch(
        saveLoadSlice.actions.fileSaved({ export: true, name: baseName }),
      );
    } catch (e) {
      log.error(e);
      dispatch(AppActions.showError({ message: e.message }));
    }
    // clear screenshot data
    dispatch(GradingActions.setMarkedScreenshots([]));
  };
}

export function ConvertJSONToCSV(results) {
  var fields = Object.keys(results[0]);
  var replacer = function (key, value) {
    return value === null ? '' : value;
  };
  var csv = results.map(function (row) {
    return fields
      .map(function (fieldName) {
        return JSON.stringify(row[fieldName], replacer);
      })
      .join(',');
  });
  csv.unshift(fields.join(','));
  csv = csv.join('\r\n');
  return csv;
}
